/**
 * P0 — Day-2 Return panel. Surfaces five "reasons to come back tomorrow"
 * in a single strip: streak, nation rank, next match countdown, pending
 * challenges, accumulated points. Pure client; pulls from existing
 * localStorage utilities + a single nation_scores read (already cached
 * by other widgets). No new tables / APIs.
 */
import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Flame, Globe2, Timer, Swords, Trophy } from "lucide-react";
import { loadStreak } from "@/lib/retention/streaks";
import { listChallenges } from "@/lib/viralSprintV3/challengeFriend";
import { useFanPoints } from "@/hooks/useFanPoints";
import { useSelectedNation } from "@/hooks/useSelectedNation";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/audit";

function formatCountdown(ms: number): string {
  if (ms <= 0) return "ya";
  const h = Math.floor(ms / 3_600_000);
  if (h >= 24) return `${Math.floor(h / 24)}d`;
  if (h >= 1) return `${h}h`;
  const m = Math.max(1, Math.floor(ms / 60_000));
  return `${m}m`;
}

export function Day2ReturnPanel() {
  const { nation } = useSelectedNation();
  const { points } = useFanPoints();
  const [streakDays, setStreakDays] = useState(0);
  const [rank, setRank] = useState<number | null>(null);
  const [pendingChallenges, setPendingChallenges] = useState(0);
  const [nextMs, setNextMs] = useState<number | null>(null);

  useEffect(() => {
    try { setStreakDays(loadStreak().consecutive_days_active); } catch { /* noop */ }
    try {
      const open = listChallenges().filter((c) => c.status === "sent" || c.status === "accepted");
      setPendingChallenges(open.length);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    if (!nation) return;
    let alive = true;
    (async () => {
      const { data } = await supabase
        .from("nation_scores")
        .select("nation_code")
        .order("total_points", { ascending: false });
      if (!alive) return;
      const idx = (data ?? []).findIndex((r) => r.nation_code === nation);
      setRank(idx >= 0 ? idx + 1 : null);
    })();
    return () => { alive = false; };
  }, [nation]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await supabase
          .from("bracket_matches")
          .select("kickoff_at")
          .gt("kickoff_at", new Date().toISOString())
          .order("kickoff_at", { ascending: true })
          .limit(1);
        if (!alive) return;
        const k = data?.[0]?.kickoff_at;
        if (k) setNextMs(new Date(k).getTime() - Date.now());
      } catch { /* noop */ }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    void trackEvent("day2_return_panel_shown", {
      streak: streakDays,
      rank,
      pending: pendingChallenges,
      points,
    });
  }, [streakDays, rank, pendingChallenges, points]);

  const trk = (tile: string) =>
    void trackEvent("day2_return_panel_tile_clicked", { tile });

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
      <Link
        to="/ambassadors"
        onClick={() => trk("streak")}
        className="rounded-xl border border-[oklch(0.78_0.2_50)]/40 bg-[oklch(0.78_0.2_50)]/10 p-2 transition hover:bg-[oklch(0.78_0.2_50)]/20"
      >
        <div className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-[oklch(0.78_0.2_50)]">
          <Flame className="h-3 w-3" /> Racha
        </div>
        <div className="mt-0.5 text-lg font-black tabular-nums">{streakDays}d</div>
      </Link>
      <Link
        to="/nation-wars"
        onClick={() => trk("nation")}
        className="rounded-xl border border-primary/40 bg-primary/10 p-2 transition hover:bg-primary/20"
      >
        <div className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-primary">
          <Globe2 className="h-3 w-3" /> Nación
        </div>
        <div className="mt-0.5 text-lg font-black tabular-nums">
          {rank ? `#${rank}` : "—"}
        </div>
      </Link>
      <Link
        to="/quinielas"
        onClick={() => trk("next")}
        className="rounded-xl border border-border bg-accent/30 p-2 transition hover:bg-accent/50"
      >
        <div className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-muted-foreground">
          <Timer className="h-3 w-3" /> Próx. predicción
        </div>
        <div className="mt-0.5 text-lg font-black tabular-nums">
          {nextMs === null ? "—" : formatCountdown(nextMs)}
        </div>
      </Link>
      <Link
        to="/quinielas"
        onClick={() => trk("challenges")}
        className="rounded-xl border border-border bg-accent/30 p-2 transition hover:bg-accent/50"
      >
        <div className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-muted-foreground">
          <Swords className="h-3 w-3" /> Retos
        </div>
        <div className="mt-0.5 text-lg font-black tabular-nums">{pendingChallenges}</div>
      </Link>
      <Link
        to="/ambassadors"
        onClick={() => trk("points")}
        className="rounded-xl border border-[oklch(0.82_0.24_145)]/40 bg-[oklch(0.82_0.24_145)]/10 p-2 transition hover:bg-[oklch(0.82_0.24_145)]/20"
      >
        <div className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-[oklch(0.82_0.24_145)]">
          <Trophy className="h-3 w-3" /> Puntos
        </div>
        <div className="mt-0.5 text-lg font-black tabular-nums">{points.toLocaleString()}</div>
      </Link>
    </div>
  );
}
